import { toApiStatus, toUiStatus } from './_helpers';
import { equipmentFromApi } from './equipment.model';

/** API Indent ↔ UI indent fields (line nested for existing screens). */

function toDateOnly(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value);
}

function equipmentTypeName(row) {
  const type = row?.equipmentType;
  if (!type) return '';
  if (typeof type === 'string') return type;
  return type.name || type.code || '';
}

function recommendationFromApi(row) {
  if (!row) return null;
  const equipment = row.equipment ? equipmentFromApi(row.equipment) : null;
  const remark = row.remark ?? '';
  const note = row.note ?? '';
  return {
    id: row.id,
    recId: row.id != null ? `REC-${row.id}` : '',
    assetId: equipment?.assetId || row.equipment?.code || '',
    assetDbId: row.equipmentId ?? equipment?.id ?? null,
    recommendedAt: row.recommendedAt ?? row.createdAt ?? null,
    recommendedBy: row.recommendedByStaff?.fullName || '',
    note,
    remark,
    photoUrl: row.photoUrl || null,
    photoUrls: Array.isArray(row.photoUrls) ? row.photoUrls : [],
    status: toUiStatus(row.status),
    equipment,
    equipmentName:
      equipment?.equipmentDescription || equipment?.equipmentType || '',
    equipmentType: equipment?.equipmentType || '',
    regNo: equipment?.regNo || '',
    manufacturer: equipment?.manufacturer || '',
    model: equipment?.model || '',
  };
}

function assignmentFromApi(row) {
  if (!row) return null;
  const equipment = row.equipment ? equipmentFromApi(row.equipment) : null;
  const actor = row.assignedByActor || '';
  const assignedByActor =
    actor === 'vendor_portal'
      ? 'Vendor'
      : actor === 'staff'
        ? 'Team'
        : actor
          ? toUiStatus(actor)
          : '';
  return {
    id: row.id,
    assetId: equipment?.assetId || row.equipment?.code || row.equipmentId || '',
    assetDbId: row.equipmentId ?? equipment?.id ?? null,
    assignedAt: row.assignedAt ?? row.createdAt ?? null,
    assignedByActor,
    assignedByActorRaw: actor || '',
    assignedBy:
      row.assignedByLabel ||
      row.assignedByStaff?.fullName ||
      (actor === 'vendor_portal' ? 'Vendor portal' : '') ||
      '',
    releasedAt: row.releasedAt ?? null,
    releasedBy:
      row.releasedByLabel ||
      row.releasedByStaff?.fullName ||
      row.releasedBy ||
      '',
    releasedByActor: row.releasedByActor || '',
    note: row.note ?? '',
    releaseNote: row.releaseNote ?? '',
    releaseRating: row.releaseRating != null ? Number(row.releaseRating) : null,
    status: toUiStatus(row.status),
    equipment,
  };
}

export function indentFromApi(row) {
  if (!row) return null;
  const indent = row.indent || row;
  const recommendations = (row.recommendations || indent.recommendations || []).map(
    recommendationFromApi,
  );
  const assignment = assignmentFromApi(row.assignment || indent.assignment);

  const typeName = equipmentTypeName(indent);
  const vendorCode = indent.vendor?.code || '';
  const buCode = indent.businessUnit?.code || '';
  const siteCode = indent.site?.code || '';

  return {
    id: indent.id,
    indentId: indent.code ?? '',
    vendorId: vendorCode,
    vendorDbId: indent.vendorId ?? indent.vendor?.id ?? null,
    vendorName: indent.vendor?.tradeName || indent.vendor?.tradeAgencyName || '',
    vendorLocalProfessional: indent.vendor?.localProfessional || '',
    buId: buCode,
    buDbId: indent.buId ?? indent.businessUnit?.id ?? null,
    buName:
      indent.businessUnit?.shortName ||
      indent.businessUnit?.code ||
      '',
    siteId: siteCode,
    siteDbId: indent.siteId ?? indent.site?.id ?? null,
    siteName: indent.site?.siteName || '',

    workArea: indent.workArea ?? '',
    raisedByName: indent.raisedByName ?? '',
    raisedByEmail: indent.raisedByEmail ?? '',
    createdAt: indent.createdAt ?? null,
    status: toUiStatus(indent.status),
    notes: indent.notes ?? '',
    line: {
      lineId: indent.code ? `${indent.code}-L1` : '',
      equipmentTypeId: indent.equipmentTypeId ?? indent.equipmentType?.id ?? null,
      equipmentType: typeName,
      specification: indent.specification ?? '',
      quantity: indent.quantity ?? 1,
      uom: indent.uom ?? '',
      capacity: indent.capacity ?? '',
      capacityUom: indent.capacityUom ?? '',
      requiredFrom: toDateOnly(indent.requiredFrom),
      requiredTill: toDateOnly(indent.requiredTill),
      timePeriod: indent.timePeriod ?? '',
      workingHours: indent.workingHours ?? '',
      remarks: indent.remarks ?? '',
    },
    recommendations,
    assignment,
    recommendationCount:
      indent.recommendationCount != null
        ? Number(indent.recommendationCount)
        : recommendations.length,
    assignmentSummary: indent.assignmentSummary || row.assignmentSummary || null,
    equipment: indent.equipment ? equipmentFromApi(indent.equipment) : null,
    active: indent.isActive !== false,
    updatedAt: indent.updatedAt ?? null,
  };
}

export function indentToApi(ui) {
  if (!ui) return null;
  const line = ui.line || {};
  return {
    vendorId: ui.vendorDbId ?? ui.vendorId,
    buId: ui.buDbId ?? ui.buId,
    siteId: ui.siteDbId ?? ui.siteId,
    workArea: ui.workArea || null,
    raisedByName: ui.raisedByName || null,
    raisedByEmail: ui.raisedByEmail || null,
    notes: ui.notes || null,
    equipmentTypeId: line.equipmentTypeId,
    specification: line.specification || null,
    quantity: line.quantity ?? 1,
    uom: line.uom || null,
    capacity: line.capacity || null,
    capacityUom: line.capacityUom || null,
    requiredFrom: line.requiredFrom,
    requiredTill: line.requiredTill,
    timePeriod: line.timePeriod || null,
    workingHours: line.workingHours || null,
    remarks: line.remarks || null,
    status: ui.status ? toApiStatus(ui.status) : undefined,
  };
}
